import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Propcode mapping version entity (matches backend PropcodeMappingVersionResponse)
 */
export type TPropcodeVersion = {
    id: string;
    dealId: string;
    versionNumber: number;
    sourceType: "upload" | "snapshot";
    storagePath: string | null;
    originalFilename: string | null;
    createdById: string;
    createdByName: string | null;
    mappingsCount: number;
    description: string | null;
    createdAt: string;
};

/**
 * Paginated propcode versions response
 */
export type TPaginatedPropcodeVersions = {
    items: TPropcodeVersion[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
};

/**
 * Version upload result (matches backend VersionUploadResponse)
 */
export type TVersionUploadResult = {
    success: boolean;
    errors: string[];
    warnings: string[];
    versionId: string | null;
    versionNumber: number | null;
    mappingsCount: number;
    storagePath: string | null;
};

/**
 * Version restore result (matches backend VersionRestoreResponse)
 */
export type TVersionRestoreResult = {
    success: boolean;
    errors: string[];
    warnings: string[];
    mappingsDeleted: number;
    mappingsCreated: number;
};

/**
 * Propcode versions slice state
 */
export type TPropcodeVersionsState = {
    // Version list
    versionsListState: TAsyncState<TPaginatedPropcodeVersions, string>;
    versionSelectedDealId: string | null;

    // Upload operation
    versionUploadState: TAsyncState<TVersionUploadResult, string>;

    // Snapshot operation
    snapshotState: TAsyncState<TVersionUploadResult, string>;

    // Restore operation
    restoreState: TAsyncState<TVersionRestoreResult, string>;
};

/**
 * Propcode versions slice actions
 */
export type TPropcodeVersionsActions = {
    // List versions
    setVersionsLoading: () => void;
    setVersionsList: (data: TPaginatedPropcodeVersions) => void;
    setVersionsError: (error: string) => void;
    setVersionSelectedDealId: (dealId: string | null) => void;

    // Upload version
    setVersionUploadLoading: () => void;
    setVersionUploadSuccess: (result: TVersionUploadResult) => void;
    setVersionUploadError: (error: string) => void;
    resetVersionUploadState: () => void;

    // Create snapshot
    setSnapshotLoading: () => void;
    setSnapshotSuccess: (result: TVersionUploadResult) => void;
    setSnapshotError: (error: string) => void;
    resetSnapshotState: () => void;

    // Restore version
    setRestoreLoading: () => void;
    setRestoreSuccess: (result: TVersionRestoreResult) => void;
    setRestoreError: (error: string) => void;
    resetRestoreState: () => void;

    // Reset all
    resetPropcodeVersionsState: () => void;
};

/**
 * Complete propcode versions slice
 */
export type TPropcodeVersionsSlice = TPropcodeVersionsState & TPropcodeVersionsActions;

/**
 * Initial state
 */
const initialState: TPropcodeVersionsState = {
    versionsListState: initialAsyncState<TPaginatedPropcodeVersions, string>(),
    versionSelectedDealId: null,
    versionUploadState: initialAsyncState<TVersionUploadResult, string>(),
    snapshotState: initialAsyncState<TVersionUploadResult, string>(),
    restoreState: initialAsyncState<TVersionRestoreResult, string>(),
};

/**
 * Create propcode versions slice
 */
export const createSlicePropcodeVersions = (): TStateCreator<TPropcodeVersionsSlice> => {
    return (set) => ({
        ...initialState,

        // List versions
        setVersionsLoading: () =>
            set((state) => ({
                versionsListState: pendingAsyncState<TPaginatedPropcodeVersions, string>(
                    state.versionsListState.data,
                ),
            })),

        setVersionsList: (data) =>
            set(() => ({
                versionsListState: fulfilledAsyncState<TPaginatedPropcodeVersions, string>(data),
            })),

        setVersionsError: (error) =>
            set((state) => ({
                versionsListState: rejectedAsyncState<TPaginatedPropcodeVersions, string>(
                    error,
                    state.versionsListState.data,
                ),
            })),

        setVersionSelectedDealId: (dealId) =>
            set(() => ({
                versionSelectedDealId: dealId,
            })),

        // Upload version
        setVersionUploadLoading: () =>
            set((state) => ({
                versionUploadState: pendingAsyncState<TVersionUploadResult, string>(
                    state.versionUploadState.data,
                ),
            })),

        setVersionUploadSuccess: (result) =>
            set(() => ({
                versionUploadState: fulfilledAsyncState<TVersionUploadResult, string>(result),
            })),

        setVersionUploadError: (error) =>
            set((state) => ({
                versionUploadState: rejectedAsyncState<TVersionUploadResult, string>(
                    error,
                    state.versionUploadState.data,
                ),
            })),

        resetVersionUploadState: () =>
            set(() => ({
                versionUploadState: initialAsyncState<TVersionUploadResult, string>(),
            })),

        // Create snapshot
        setSnapshotLoading: () =>
            set((state) => ({
                snapshotState: pendingAsyncState<TVersionUploadResult, string>(
                    state.snapshotState.data,
                ),
            })),

        setSnapshotSuccess: (result) =>
            set(() => ({
                snapshotState: fulfilledAsyncState<TVersionUploadResult, string>(result),
            })),

        setSnapshotError: (error) =>
            set((state) => ({
                snapshotState: rejectedAsyncState<TVersionUploadResult, string>(
                    error,
                    state.snapshotState.data,
                ),
            })),

        resetSnapshotState: () =>
            set(() => ({
                snapshotState: initialAsyncState<TVersionUploadResult, string>(),
            })),

        // Restore version
        setRestoreLoading: () =>
            set((state) => ({
                restoreState: pendingAsyncState<TVersionRestoreResult, string>(
                    state.restoreState.data,
                ),
            })),

        setRestoreSuccess: (result) =>
            set(() => ({
                restoreState: fulfilledAsyncState<TVersionRestoreResult, string>(result),
            })),

        setRestoreError: (error) =>
            set((state) => ({
                restoreState: rejectedAsyncState<TVersionRestoreResult, string>(
                    error,
                    state.restoreState.data,
                ),
            })),

        resetRestoreState: () =>
            set(() => ({
                restoreState: initialAsyncState<TVersionRestoreResult, string>(),
            })),

        // Reset all
        resetPropcodeVersionsState: () => set(() => initialState),
    });
};
